import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Mail, ArrowRight, BookOpen, Calendar } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NewsletterPage() {
  const [email, setEmail] = useState("");
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [newsletters, setNewsletters] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [loading, setLoading] = useState(true);

  const fetchNewsletters = useCallback(async () => {
    try {
      const params = { is_newsletter: true };
      if (selectedCategory !== "All") {
        params.category = selectedCategory;
      }
      const response = await axios.get(`${API}/articles`, { params });
      setNewsletters(response.data);
    } catch (error) {
      console.error("Failed to fetch newsletters:", error);
    } finally {
      setLoading(false);
    }
  }, [selectedCategory]);

  useEffect(() => {
    fetchNewsletters();
  }, [fetchNewsletters]);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setIsSubscribing(true);
    try {
      await axios.post(`${API}/newsletter/subscribe`, { email });
      toast.success("Successfully subscribed!");
      setEmail("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to subscribe");
    } finally {
      setIsSubscribing(false);
    }
  };

  const handleCategoryClick = (cat) => {
    setSelectedCategory(cat);
  };

  const cats = ["All", "Agentic AI", "Contact Center AI", "Enterprise AI Strategy", "Tutorials", "Leadership Insights"];

  return (
    <div className="pt-20" data-testid="newsletter-page">
      <section className="py-24 md:py-32 cta-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-2xl mx-auto text-center animate-fade-in-up">
            <Mail className="mx-auto mb-6 opacity-80" size={48} />
            <h1 className="font-display text-4xl md:text-5xl font-bold mb-6">AI Newsletter</h1>
            <p className="text-blue-100 text-lg mb-8">Get weekly insights on AI strategy delivered to your inbox.</p>
            <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1 bg-white/10 border-white/20 text-white placeholder:text-blue-200"
                data-testid="newsletter-email-input"
              />
              <Button type="submit" disabled={isSubscribing} className="bg-white text-royal-blue-800 hover:bg-blue-50 px-8" data-testid="newsletter-submit-btn">
                {isSubscribing ? "..." : "Subscribe"}
              </Button>
            </form>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="mb-12">
            <span className="text-xs font-semibold tracking-widest uppercase text-royal-blue-800 mb-4 block">Archive</span>
            <h2 className="font-display text-3xl md:text-4xl font-semibold text-zinc-950 mb-4">Past Newsletters</h2>
          </div>

          <div className="flex flex-wrap gap-2 mb-12">
            {cats.map((cat) => (
              <button
                key={cat}
                onClick={() => handleCategoryClick(cat)}
                className={`px-4 py-2 text-sm font-medium rounded-full transition-all ${selectedCategory === cat ? "bg-royal-blue-800 text-white" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"}`}
              >
                {cat}
              </button>
            ))}
          </div>

          {loading && <p className="text-center text-zinc-500 py-12">Loading...</p>}
          
          {!loading && newsletters.length === 0 && (
            <div className="text-center py-16 bg-zinc-50 rounded-sm">
              <BookOpen className="mx-auto mb-4 text-zinc-400" size={48} />
              <h3 className="font-display text-xl font-semibold text-zinc-950 mb-2">No Newsletters Yet</h3>
              <p className="text-zinc-600">Subscribe to be the first to know.</p>
            </div>
          )}

          {!loading && newsletters.length > 0 && (
            <div className="space-y-6">
              {newsletters.map((nl, i) => (
                <article key={nl.id} className="article-card p-6 md:p-8 rounded-sm" data-testid={`newsletter-item-${i}`}>
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-3">
                        <Badge variant="secondary" className="bg-royal-blue-100 text-royal-blue-800">{nl.category}</Badge>
                        <span className="text-sm text-zinc-500 flex items-center gap-1">
                          <Calendar size={14} />
                          {new Date(nl.published_at).toLocaleDateString()}
                        </span>
                      </div>
                      <Link to={`/blog/${nl.slug}`}>
                        <h3 className="font-display text-xl md:text-2xl font-semibold text-zinc-950 mb-2 hover:text-royal-blue-800">{nl.title}</h3>
                      </Link>
                      <p className="text-zinc-600 mb-4">{nl.excerpt}</p>
                    </div>
                    <Link to={`/blog/${nl.slug}`} className="flex items-center gap-2 text-sm font-medium text-royal-blue-800">Read more <ArrowRight size={16} /></Link>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="py-24 md:py-32 section-muted">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <span className="text-xs font-semibold tracking-widest uppercase text-royal-blue-800 mb-4 block">Topics</span>
            <h2 className="font-display text-3xl md:text-4xl font-semibold text-zinc-950">What You'll Learn</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="p-6 bg-white border border-zinc-200 rounded-sm"><h3 className="font-display text-lg font-semibold text-zinc-950 mb-2">Agentic AI</h3><p className="text-zinc-600 text-sm">Autonomous AI systems</p></div>
            <div className="p-6 bg-white border border-zinc-200 rounded-sm"><h3 className="font-display text-lg font-semibold text-zinc-950 mb-2">Contact Center AI</h3><p className="text-zinc-600 text-sm">Customer experience</p></div>
            <div className="p-6 bg-white border border-zinc-200 rounded-sm"><h3 className="font-display text-lg font-semibold text-zinc-950 mb-2">Enterprise AI Strategy</h3><p className="text-zinc-600 text-sm">Building AI roadmaps</p></div>
            <div className="p-6 bg-white border border-zinc-200 rounded-sm"><h3 className="font-display text-lg font-semibold text-zinc-950 mb-2">Tutorials</h3><p className="text-zinc-600 text-sm">Implementation guides</p></div>
            <div className="p-6 bg-white border border-zinc-200 rounded-sm"><h3 className="font-display text-lg font-semibold text-zinc-950 mb-2">Leadership Insights</h3><p className="text-zinc-600 text-sm">AI transformation</p></div>
            <div className="p-6 bg-white border border-zinc-200 rounded-sm"><h3 className="font-display text-lg font-semibold text-zinc-950 mb-2">Industry Trends</h3><p className="text-zinc-600 text-sm">Latest developments</p></div>
          </div>
        </div>
      </section>
    </div>
  );
}
